import Aes from 'react-native-aes-crypto';

import { constantTimeEqualHex } from './DocumentEncryptionV2';

/**
 * K5 — a saved login's fields, encrypted on the device
 * (docs/DESIGN_K5_credential-encryption.md §3.2).
 *
 * One field value in, one string out:
 *
 *   fkc1:<iv, 32 hex>:<ciphertext, base64>:<tag, 64 hex>
 *
 * AES-256-CBC with a fresh IV, authenticated by HMAC-SHA-256 over
 * (prefix || iv || ciphertext), tag checked before any decryption. The same
 * native surface (and the same web shim) DocumentEncryptionV2 and
 * KeyWrapping already use, so nothing new is audited.
 *
 * The key is not the master key itself but a credential key derived from
 * it, so credential ciphertext and wrapped document keys live in separate
 * domains:
 *
 *   credEnc = HMAC-SHA-256(key = masterKey, msg = "mfk.cred.v1.enc")
 *   credMac = HMAC-SHA-256(key = masterKey, msg = "mfk.cred.v1.mac")
 *
 * `:` never occurs in hex or base64, so a split is a parse. The prefix is
 * the version; a new construction gets `fkc2:`.
 */

export const CREDENTIAL_CIPHERTEXT_PREFIX = 'fkc1:';

const CRED_INFO_ENC = 'mfk.cred.v1.enc';
const CRED_INFO_MAC = 'mfk.cred.v1.mac';

// The master key: 32-byte AES half + 32-byte HMAC half, 128 hex chars.
const MASTER_KEY_HEX_LEN = 128;
// The derived credential key has the same shape.
const ENC_KEY_HEX_LEN = 64;
const CRED_KEY_HEX_LEN = 128;

const IV_BYTES = 16;
const IV_HEX_LEN = IV_BYTES * 2;
const TAG_HEX_LEN = 64;

/** The server refuses longer values (registry MAX_CREDENTIAL_FIELD_CHARS). */
export const MAX_CREDENTIAL_PLAINTEXT_CHARS = 2048;

const HEX = /^[0-9a-f]+$/;
const BASE64 = /^[A-Za-z0-9+/]+=*$/;

export class CredentialDecryptError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CredentialDecryptError';
  }
}

export class CredentialEncryptError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CredentialEncryptError';
  }
}

function assertKey(keyHex: string, expectedLen: number, what: string): void {
  if (typeof keyHex !== 'string' || keyHex.length !== expectedLen || !HEX.test(keyHex.toLowerCase())) {
    throw new CredentialEncryptError(`${what} must be ${expectedLen} hex chars`);
  }
}

function splitCredentialKey(credentialKey: string): { encKey: string; macKey: string } {
  assertKey(credentialKey, CRED_KEY_HEX_LEN, 'credential key');
  return {
    encKey: credentialKey.slice(0, ENC_KEY_HEX_LEN),
    macKey: credentialKey.slice(ENC_KEY_HEX_LEN),
  };
}

/**
 * Derive the credential key from the account's master key. Deterministic,
 * so callers may cache the result for the session; never persist it.
 */
export async function deriveCredentialKey(masterKey: string): Promise<string> {
  assertKey(masterKey, MASTER_KEY_HEX_LEN, 'master key');
  const encHalf = await Aes.hmac256(CRED_INFO_ENC, masterKey);
  const macHalf = await Aes.hmac256(CRED_INFO_MAC, masterKey);
  return encHalf + macHalf;
}

/** True for a string in the `fkc1:` form (shape only; the MAC decides). */
export function isCredentialCiphertext(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith(CREDENTIAL_CIPHERTEXT_PREFIX);
}

export interface CredentialCiphertextParts {
  iv: string;
  ciphertext: string;
  tag: string;
}

/** Split a `fkc1:` value into its parts. Throws on any shape defect. */
export function parseCredentialCiphertext(value: string): CredentialCiphertextParts {
  if (!isCredentialCiphertext(value)) {
    throw new CredentialDecryptError('not a credential ciphertext');
  }
  const parts = value.split(':');
  if (parts.length !== 4) {
    throw new CredentialDecryptError('malformed credential ciphertext');
  }
  const [, iv, ciphertext, tag] = parts;
  if (iv.length !== IV_HEX_LEN || !HEX.test(iv)) {
    throw new CredentialDecryptError('malformed iv');
  }
  if (ciphertext.length === 0 || !BASE64.test(ciphertext)) {
    throw new CredentialDecryptError('malformed ciphertext');
  }
  if (tag.length !== TAG_HEX_LEN || !HEX.test(tag)) {
    throw new CredentialDecryptError('malformed tag');
  }
  return { iv, ciphertext, tag };
}

/**
 * Encrypt one field. An empty string stays empty (nothing to protect, and
 * "does this record have a login?" checks keep working). Everything else
 * becomes ciphertext, whatever it looks like.
 */
export async function encryptField(plaintext: string, credentialKey: string): Promise<string> {
  if (typeof plaintext !== 'string') {
    throw new CredentialEncryptError('plaintext must be a string');
  }
  if (plaintext === '') return '';
  if (plaintext.length > MAX_CREDENTIAL_PLAINTEXT_CHARS) {
    throw new CredentialEncryptError(`value longer than ${MAX_CREDENTIAL_PLAINTEXT_CHARS} characters`);
  }
  const { encKey, macKey } = splitCredentialKey(credentialKey);
  const iv = await Aes.randomKey(IV_BYTES);
  const ciphertext = await Aes.encrypt(plaintext, encKey, iv, 'aes-256-cbc');
  const tag = await Aes.hmac256(CREDENTIAL_CIPHERTEXT_PREFIX + iv + ciphertext, macKey);
  return `${CREDENTIAL_CIPHERTEXT_PREFIX}${iv}:${ciphertext}:${tag}`;
}

/**
 * Decrypt one field. The tag is checked first; a wrong key, a changed
 * byte or a malformed value is a CredentialDecryptError, never a guess.
 */
export async function decryptField(value: string, credentialKey: string): Promise<string> {
  const { iv, ciphertext, tag } = parseCredentialCiphertext(value);
  const { encKey, macKey } = splitCredentialKey(credentialKey);
  const expected = await Aes.hmac256(CREDENTIAL_CIPHERTEXT_PREFIX + iv + ciphertext, macKey);
  if (!constantTimeEqualHex(expected, tag)) {
    throw new CredentialDecryptError('authentication tag mismatch');
  }
  try {
    return await Aes.decrypt(ciphertext, encKey, iv, 'aes-256-cbc');
  } catch {
    throw new CredentialDecryptError('ciphertext decryption failed');
  }
}
